import React from "react";
import { Link } from "react-router-dom";
import { useRankings } from "../apiCalls/userApiCalls";
import {
  Container,
  Card,
  CountryLabel,
  RankBadge,
  RankMovement,
  VerifiedTick,
} from "./ui";
import { ArrowRightIcon } from "./ui/icons";
import { formatNumber } from "../utils";
import Loader from "./Loader";
import ErrorState from "./ErrorState";
import EmptyState from "./EmptyState";

// The home page leaderboard: a clean top-5, not a wall of medal cards. The full
// table lives on /rankings.
const Rankings = () => {
  const { data, isFetching, isError } = useRankings(1, "", "", "", 5);
  const players = data?.data ?? [];

  return (
    <section className="bg-bg py-16 md:py-20">
      <Container>
        <div className="mb-6 flex items-end justify-between gap-4">
          <h2 className="text-2xl font-bold tracking-tight text-navy md:text-3xl">
            Top Ranked Players
          </h2>
          <Link
            to="/rankings"
            className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary-hover"
          >
            View full rankings
            <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>

        {isFetching && !data && (
          <Card>
            <Loader label="Loading rankings…" />
          </Card>
        )}
        {isError && (
          <Card>
            <ErrorState message="Could not load rankings. Please try again." />
          </Card>
        )}
        {!isFetching && !isError && players.length === 0 && (
          <Card>
            <EmptyState message="No ranked players yet." />
          </Card>
        )}

        {players.length > 0 && (
          <Card padded={false} className="overflow-hidden">
            <ul className="divide-y divide-border">
              {players.map((player) => (
                <li key={player._id}>
                  <Link
                    to={`/players/${player.slug || player._id}`}
                    className="group flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-bg md:gap-4 md:px-5"
                  >
                    <RankBadge rank={player.ranking} />
                    <RankMovement
                      rank={player.ranking}
                      previousRank={player.previousRank}
                      className="hidden w-10 sm:flex"
                    />
                    <img
                      src={
                        player.imgUrl ||
                        `${process.env.PUBLIC_URL}/default-profile-image.jpg`
                      }
                      alt=""
                      className="h-10 w-10 rounded-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `${process.env.PUBLIC_URL}/default-profile-image.jpg`;
                      }}
                    />
                    <CountryLabel
                      code={player.nationality}
                      variant="flag"
                      className="shrink-0"
                    />
                    <span className="min-w-0 flex-1 truncate font-semibold text-navy group-hover:text-primary">
                      {player.name}
                      {player.isClaimed && (
                        <VerifiedTick className="ml-1.5 inline h-4 w-4 align-text-bottom" />
                      )}
                    </span>
                    <span className="tabular shrink-0 font-bold text-navy">
                      {formatNumber(player.rankingPoints)}{" "}
                      <span className="text-xs font-medium text-muted">pts</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </Container>
    </section>
  );
};

export default Rankings;
